import { useState } from 'react';

import { MerchantShowAccountsForm } from 'pages/Merchants/MerchantsShow/MerchantShowAccounts/components/MerchantShowAccountsForm/MerchantShowAccountsForm';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, useCreate, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';

import { useIsMerchantIdEnabledByDefault } from './hooks/useIsMerchantIdEnabledByDefault';
import { CreateStyled } from './MerchantShowAccountsCreateDialog.styled';
import { MerchantShowAccountsFormSkeleton } from '../components/MerchantShowAccountsForm/MerchantShowAccountsFormSkeleton';

type Props = {
  open: boolean;
  onClose: () => void;
  merchantId?: string;
  merchantPlatformId?: string;
};

interface CreateFormValues extends FieldValues {
  merchant: string;
  provider: string;
  gateway: string;
  name: string;
  credentials: Record<string, any>;
}

export const MerchantShowAccountsCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  merchantId,
  merchantPlatformId,
}) => {
  const translate = useTypedTranslate();

  const [create] = useCreate();
  const notify = useNotify();
  const [errors, setErrors] = useState<string[]>([]);
  const { isMerchantIdEnabledByDefault, isLoading } =
    useIsMerchantIdEnabledByDefault(merchantPlatformId);

  const onSubmit = (formValues: CreateFormValues) => {
    create(
      'merchant_accounts',
      {
        data: {
          ...formValues,
          merchants: [merchantId],
        },
      },
      {
        onSuccess: () => {
          notify(translate('app.merchantAccounts.merchantAccountCreatedSuccessfully'), {
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          setErrors((error as Error)?.message.split('\n'));
          notify(`${translate('app.common.error')}: ${(error as Error).message}`, {
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <Dialog maxWidth="lg" onClose={onClose} open={open}>
      <CreateStyled resource="merchant_accounts" sx={{ margin: 0 }}>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.merchantAccounts.createAccount')}
          titleSize="smallBold"
        />
        <Form onSubmit={onSubmit as any}>
          <CardContent>
            {isLoading ? (
              <MerchantShowAccountsFormSkeleton />
            ) : (
              <MerchantShowAccountsForm
                errors={errors}
                merchantId={merchantId}
                merchantIdEnabledDefaultValue={isMerchantIdEnabledByDefault}
              />
            )}
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <Button
                key="create"
                label={translate('app.buttons.create')}
                type="submit"
                variant="contained"
              />,
            ]}
          />
        </Form>
      </CreateStyled>
    </Dialog>
  );
};
